/**
 * WebSocket Client (Native WebSocket API)
 * Compatible with backend @fastify/websocket implementation
 */

import { WS_URL, WS_CHANNELS } from './constants';
import { getAccessToken } from './api';
import { logger } from './logger';

export type WSChannel = typeof WS_CHANNELS[keyof typeof WS_CHANNELS];
export { WS_CHANNELS };

export type WSEventHandler = (data: any) => void;

interface ClientMessage {
  type: 'subscribe' | 'unsubscribe' | 'heartbeat' | 'ack';
  payload?: any;
}

interface ServerMessage {
  id: string;
  event: {
    type: string;
    channel: string;
    timestamp: string;
    payload: any;
  };
  requiresAck: boolean;
}

class WebSocketClient {
  private socket: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectDelay = 1000;
  private isConnecting = false;
  private subscriptions: Map<string, Set<WSEventHandler>> = new Map();
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;

  connect() {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      logger.info('WebSocket already connected');
      return;
    }

    if (this.isConnecting) {
      logger.info('WebSocket already connecting');
      return;
    }

    this.isConnecting = true;
    const token = getAccessToken();

    if (!token) {
      logger.error('Cannot connect to WebSocket: No access token', new Error('No access token'));
      this.isConnecting = false;
      return;
    }

    // Build WebSocket URL with token as query param
    // Handle both ws://host:port and ws://host:port/ws formats to avoid double /ws/ws
    let baseUrl = WS_URL.replace('http://', 'ws://').replace('https://', 'wss://');
    if (baseUrl.endsWith('/ws')) {
      baseUrl = baseUrl.slice(0, -3); // Remove trailing /ws if present
    }
    const wsUrl = `${baseUrl}/ws?token=${token}`;
    logger.info('Initializing WebSocket connection', { url: wsUrl });

    try {
      this.socket = new WebSocket(wsUrl);

      this.socket.onopen = () => {
        logger.info('WebSocket connected');
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000;
        this.isConnecting = false;

        // Start heartbeat
        this.startHeartbeat();

        // Resubscribe to channels
        this.subscriptions.forEach((_, channel) => {
          this.sendMessage({
            type: 'subscribe',
            payload: { channel },
          });
          logger.info('Resubscribed to channel', { channel });
        });
      };

      this.socket.onmessage = (event) => {
        try {
          const message: ServerMessage = JSON.parse(event.data);
          const { event: evt } = message;

          // Handle event by channel
          const handlers = this.subscriptions.get(evt.channel);
          if (handlers) {
            handlers.forEach((handler) => {
              try {
                handler(evt.payload);
              } catch (error) {
                logger.error('Error in WebSocket handler', error as Error, { channel: evt.channel });
              }
            });
          }

          // Send acknowledgment if required
          if (message.requiresAck) {
            this.sendMessage({
              type: 'ack',
              payload: { messageId: message.id },
            });
          }
        } catch (error) {
          logger.error('Failed to parse WebSocket message', error as Error);
        }
      };

      this.socket.onclose = (event) => {
        logger.warn('WebSocket closed', { code: event.code.toString(), reason: event.reason });
        this.isConnecting = false;
        this.stopHeartbeat();

        if (event.code !== 1000) {
          // Not a normal closure, attempt reconnect
          this.scheduleReconnect();
        }
      };

      this.socket.onerror = (error) => {
        logger.error('WebSocket error', error as any);
        this.isConnecting = false;
        this.scheduleReconnect();
      };
    } catch (error) {
      logger.error('Failed to create WebSocket', error as Error);
      this.isConnecting = false;
      this.scheduleReconnect();
    }
  }

  disconnect() {
    if (this.socket) {
      this.stopHeartbeat();
      this.socket.close(1000, 'Client disconnecting');
      this.socket = null;
      logger.info('WebSocket disconnected manually');
    }
    this.subscriptions.clear();
  }

  subscribe(channel: WSChannel, handler: WSEventHandler) {
    if (!this.subscriptions.has(channel)) {
      this.subscriptions.set(channel, new Set());
    }
    this.subscriptions.get(channel)!.add(handler);

    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.sendMessage({
        type: 'subscribe',
        payload: { channel },
      });
      logger.info('Subscribed to channel', { channel });
    }
  }

  unsubscribe(channel: WSChannel, handler: WSEventHandler) {
    const handlers = this.subscriptions.get(channel);
    if (handlers) {
      handlers.delete(handler);
      if (handlers.size === 0) {
        this.subscriptions.delete(channel);
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
          this.sendMessage({
            type: 'unsubscribe',
            payload: { channel },
          });
          logger.info('Unsubscribed from channel', { channel });
        }
      }
    }
  }

  send(channel: WSChannel, event: string, data: any) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      logger.warn('Cannot send message: WebSocket not connected', { channel, event });
      throw new Error('WebSocket not connected');
    }

    this.sendMessage({
      type: 'subscribe', // This is a placeholder, adjust based on your protocol
      payload: data,
    });
    logger.debug('Message sent', { channel, event });
  }

  isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  private sendMessage(message: ClientMessage) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    }
  }

  private startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        this.sendMessage({ type: 'heartbeat' });
        logger.debug('Heartbeat sent');
      }
    }, 30000); // Every 30 seconds
  }

  private stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      logger.error('Max reconnection attempts reached', new Error('Max reconnection attempts reached'));
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), 30000);

    logger.info('Scheduling reconnection', {
      attempt: this.reconnectAttempts.toString(),
      delay: delay.toString(),
    });

    setTimeout(() => {
      this.connect();
    }, delay);
  }
}

export const wsClient = new WebSocketClient();

export default wsClient;
